<template>
  <div class="row gx-4 gy-4 mb-4">
    <div class="col-md-3">
      <div v-bind:class="['dashboard-card', 'dashboard-card-status-overall', { 'spinner-is-visible': isSpinnerVisible }]">
        <div class="dashboard-card-header pb-0">
          <div v-if="!isSpinnerVisible" class="dashboard-card-total w-100">
            <div class="d-flex align-items-center justify-content-between w-100">
              <div class="d-flex align-items-center"><strong class="m-0 me-2">{{ overallStatuses.totalStatuses }}</strong>Assets</div>
              <div>Reject Rate<strong>{{ overallStatuses.rejectRate }}</strong></div>
            </div>
          </div>
        </div>
        <div class="dashboard-card-content">
          <BarsChart
            ref="overallStatus"
            v-if="!isSpinnerVisible" 
            title=""
            :data="overallStatuses.data"
            bars-min-width="10px"
            :relative-to-highest-value="false"
            :include-counts="true"
          />
        </div>
        <div class="spinner"></div>
      </div>
    </div>
    <div class="col-md-3">
      <div v-bind:class="['dashboard-card', 'dashboard-card-rejected-priority-poles', { 'spinner-is-visible': isSpinnerVisible }]">
        <div class="dashboard-card-header pb-0">
          <div class="dashboard-card-title">
            Rejected and Priority Poles
          </div>
        </div>
        <div class="dashboard-card-content">
          <RejectPriorityAssetsChart
            v-if="!isSpinnerVisible"
            ref="rejectedAndPriorityPoles"
            :rejected-and-priority-poles="filteredRejectedAndPriorityAssets"
          />
        </div>
        <div class="spinner"></div>
      </div>
    </div>
    <div class="col-md-6">
      <div v-bind:class="['dashboard-card', 'dashboard-card-inspection-types', { 'spinner-is-visible': isSpinnerVisible }]">
        <div class="dashboard-card-header pb-0">
          <div class="dashboard-card-title">
            Inspection Types
          </div>
          <div v-if="!isSpinnerVisible" class="dashboard-card-total">
            Total Inspections <strong>{{ inspectionTypes.totalInspectionTypes }}</strong>
          </div>
        </div>
        <div class="dashboard-card-content">
          <BarsChart
            ref="inspectionTypes"
            v-if="!isSpinnerVisible"
            :data="inspectionTypes.data"
            bars-min-width="10px"
            :relative-to-highest-value="true"
            :include-counts="true"
            :enable-select="true"
            :enable-multi-select="true"
            @stats-selection-change="onInspectionTypesSelect"
          />
        </div>
        <div class="spinner"></div>
      </div>
    </div>
  </div>
  <div class="row gx-4 gy-4 mb-4">
    <div :class="isWeeklyStatisticsVisible ? 'col-md-6' : 'col-md-12'">
      <div class="dashboard-card">
        <AssetsMap 
          ref="dashboardAssetsMap"
          :assets="filteredAssets"
          :googleMapApiKey="googleMapApiKey"
        />
      </div>
    </div>
    <div 
      v-if="isWeeklyStatisticsVisible"
      class="col-md-6"
    >
      <div class="row gy-3">
        <div class="col-sm-12">
          <div v-bind:class="['dashboard-card', 'dashboard-card-weekly-progress', {'view-grid': weeksInspections.dataViewMode == 'grid'}, {'view-graph': weeksInspections.dataViewMode == 'graph'}, { 'spinner-is-visible': isSpinnerVisible }]">
            <div class="dashboard-card-header pb-0">
              <div class="dashboard-card-title">
                Weekly progress
              </div>
              <div v-if="!isSpinnerVisible" class="d-flex align-items-center">
                <div class="dashboard-card-total">
                  Total Inspections <strong>{{ weeksInspections.inspections }}</strong>
                </div>
                <div 
                  v-if="weeksInspections.data.length > 1"
                  class="dashboard-card-toggle btn-group ps-4"
                >
                  <button :class="['btn btn-sm btn-info', {'btn-inverse-info': weeksInspections.dataViewMode == 'grid'}]" @click.prevent="toggleWeeklyProgressView('graph')">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11.5 4L13.25 5.75L9.5 9.5L6.5 6.5L0 13L1 14L6.5 8.5L9.5 11.5L14.25 6.75L16 8.5V4H11.5Z" fill="#fff"/>
                    </svg>
                  </button>
                  <button :class="['btn btn-sm btn-info', {'btn-inverse-info': weeksInspections.dataViewMode == 'graph'}]" @click.prevent="toggleWeeklyProgressView('grid')">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 3V14H16V3H0ZM6 10V8H10V10H6ZM10 11V13H6V11H10ZM10 5V7H6V5H10ZM5 5V7H1V5H5ZM1 8H5V10H1V8ZM11 8H15V10H11V8ZM11 7V5H15V7H11ZM1 11H5V13H1V11ZM11 13V11H15V13H11Z" fill="#fff"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="dashboard-card-content pt-0">
              <div class="view-grid">
                <div class="table-wrapper">
                  <div
                    :class="['table-responsive', 'table-scroll', { 'spinner-is-visible': isSpinnerVisible }]"
                  >
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Week</th>
                          <th
                            class="text-end"
                            style="width: 150px"
                          >Total</th>
                        </tr>
                      </thead>
                    </table>
                    
                    <div class="table-body" style="height: 95px;">
                      <table class="table">
                        <tbody>
                          <tr 
                            v-for="(week, index) in weeksInspections.data"
                            v-bind="week"
                            :key="'week-' + index"
                            :index="index"
                            :ref="'week-' + index"
                          >
                            <td>
                              {{ m$.utc(week.startDate).format("dddd") }}, <span>{{ m$.utc(week.startDate).format("DD MMM") }}</span>, {{ m$.utc(week.startDate).format("YYYY") }}
                            </td>
                            <td class="text-end">
                              <strong>{{ week.inspections }}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <div class="spinner"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="view-graph">
                <div>
                  <LineChart
                    ref="weeklyProgress"
                    v-if="!isSpinnerVisible"
                    :yLabels="weeksInspections.yLabels"
                    :xLabels="weeksInspections.xLabels"
                    :labels-font-size="13"
                    :height="120"
                    :data="weeksInspections.chartData"
                    :showGridXLine="false"
                    :showXlabels="true"
                  />
                </div>
              </div>
            </div>
            <div class="spinner"></div>
          </div>
        </div>
        
        <div class="col-sm-12">
          <div v-bind:class="['dashboard-card', 'dashboard-card-poles-status', {'view-all': weeksStatuses.dataViewMode == 'all'}, {'view-week': weeksStatuses.dataViewMode == 'week'}, { 'spinner-is-visible': isSpinnerVisible }]">
            <div class="dashboard-card-header pb-0">
              <div class="dashboard-card-title col-5">
                Weekly Rejected and Priority Poles
              </div>
              <div
                v-if="weeksStatuses.data.length > 1 && !isSpinnerVisible && weeksStatuses.dataViewMode == 'week'"
                class="d-flex align-items-center justify-content-center col-2" 
              >
                <a 
                  v-if="weeksStatuses.data.length > 1"
                  href="#" @click.prevent="showWeeksStatusDetails(weeksStatuses.currentWeek - 1)"
                >
                  <svg height="18" viewBox="0 0 14 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 1L2 12L13 22.9999" stroke="#000000" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </a>
                <span class="px-2">{{ h$.formatDate.dayMonth(weeksStatuses.chartData[weeksStatuses.currentWeek].week) }}</span>
                <a 
                  v-if="weeksStatuses.data.length > 1"
                  href="#" @click.prevent="showWeeksStatusDetails(weeksStatuses.currentWeek + 1)"
                >
                  <svg height="18" viewBox="0 0 14 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 22.9998L12 11.9998L1 0.999878" stroke="#000000" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </a>
              </div>
              <div class="d-flex align-items-center col-5 justify-content-end" v-if="!isSpinnerVisible">
                <div class="dashboard-card-total">
                  Total <strong>{{ weeksStatuses.chartTotal }}</strong>
                </div>
                <div 
                  v-if="weeksStatuses.data.length > 1"
                  class="dashboard-card-toggle btn-group ps-4"
                >
                  <button :class="['btn btn-sm btn-info', {'btn-inverse-info': weeksStatuses.dataViewMode == 'all'}]" @click.prevent="showWeeksStatusDetails(0)">Week</button>
                  <button :class="['btn btn-sm btn-info', {'btn-inverse-info': weeksStatuses.dataViewMode == 'week'}]" @click.prevent="toggleWeeksStatusesView('all')">Month</button>
                </div>
              </div>
            </div>
            <div class="dashboard-card-content">
              <div class="view-all">
                <div class="multi-column-chart" style="height: 120px;">
                  <div
                    v-for="(weekStatus, index) in weeksStatuses.chartData"
                    v-bind="weekStatus"
                    :key="'poles-status-' + index"
                    :index="index"
                    :ref="'poles-status-' + index"
                    class="chart-column"
                    :style="{ '--column-height': weekStatus.percentage + '%' }"
                    @click="showWeeksStatusDetails(index)"
                  >
                    <ColumnChart
                      :data="weekStatus.data"
                      :bars-min-height="'5px'"
                      :show-tooltips="false"
                    />
                  </div>
                </div>
              </div>
              <div class="view-week">
                <BarsChart
                  ref="weekStatusDetails"
                  v-if="!isSpinnerVisible"
                  :data="weeksStatuses.currentWeekData"
                  bars-min-width="10px"
                  :relative-to-highest-value="true"
                  :include-counts="true"
                />
              </div>
            </div>
            <div class="spinner"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="row">
    <div class="col-sm-12">
      <div class="dashboard-card">
        <div class="dashboard-card-content">
          <div class="grid-data-groups">
            <DataGroupsGrid 
              :data-groups="dataGroups"
              :policy-update="policyUpdate"
              :fulcrum-app-id="fulcrumApp.id"
              @dataGroupsSelectionChange="onDataGroupsSelectionChange"
              @dataGroupApproved="onDataGroupApproved"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Moment from "moment";

import Helpers from "../../../helpers.js";

import Screen from "./screen";

import RejectPriorityAssetsChart from "./reject_priority_assets_chart";

export default {
  extends: Screen,

  components: {
    RejectPriorityAssetsChart
  },

  data: function () {
    return {
      inspectionTypes: {
        chartColors: [1, 6, 13, 5, 11, 9, 2, 12, 15, 7, 8, 10],
        totalInspectionTypes: 0,
        data: []
      },
      selectedInspectionTypes: [],
      
      overallStatuses: {
        statusesMap: {
          'Visited': ['Y', 'Y-QC', 'Y-H20', 'Y-H20-QC'],
          'Rejected': ['Y-R', 'Y-R-QC', 'Y-NRR', 'Y-NRR-QC', 'Y-RR', 'Y-RR-QC', 'Y-UGR', 'Y-UGR-QC'],
          'Priority': ['Y-PRI', 'Y-PRI-QC'],
          'Not Visited': ['N', 'N-Pad', 'NI'],
        },
        chartColors: [2, 6, 13, 9],
        totalStatuses: 0,
        inspectedCount: 0,
        rejectRate: 0,
        data: []
      },

      weeksInspections: {
        xLabels: {
          title: '',
          labels: [],
          labelHeight: 15,
          offsetBottom: 0,
          offsetTop: 10
        },
        yLabels: {
          title: '',
          labels: [],
          labelWidth: 40,
          offsetLeft: 0,
          offsetRight: 10
        },
        chartData: [],
        data: [],
        dataViewMode: 'graph',
      },
      
      weeksStatuses: {
        statusChoices: [],
        data: [],
        chartTotal: 0,
        chartData: [],
        chartOrder: ['Y-UGR', 'Y-R', 'Y-RR', 'Y-NRR', 'Y-PRI'],
        chartColors: {
          2:  ['Y-UGR'],
          6:  ['Y-R'],
          9:  ['Y-RR'],
          1:  ['Y-NRR'],
          13: ['Y-PRI']
        },
        currentWeek: 0,
        currentWeekData: [],
        dataViewMode: 'all'
      },

      filteredAssets: [],
      filteredRejectedAndPriorityAssets: [],
    }
  },

  setup () {
    return {
      h$: Helpers,
      m$: Moment
    }
  },

  computed: {
    isWeeksStatusesEmpty() {
      return this.weeksStatuses.data.length === 0;
    },

    isWeeklyStatisticsVisible() {
      return (this.isInitialized && this.weeksStatuses.data.length > 0) || !this.isInitialized;
    },  
  },

  methods: {
    onAppLoaded(app) {
      this.weeksStatuses.statusChoices = app.statusField.choices || [];
    },

    setInspectionTypes() {
      // count inspection types
      function countInspectionTypes(assets) {
        return assets.reduce((acc, asset) => {
          const inspectionType = asset.inspection_type;
          if (inspectionType != null) {
            acc[inspectionType] = (acc[inspectionType] || 0) + 1;
          }
          return acc;
        }, {});
      }

      // Step 1: Count inspection types in assets
      let inspectionTypesObj = countInspectionTypes(this.allAssets);

      // Step 2: Remove empty keys eg. "uninspected" types
      Object.keys(inspectionTypesObj).forEach(key => !key && delete inspectionTypesObj[key]);

      let inspectionTypes = Object.entries(inspectionTypesObj);

      // Step 3: Aggregate by inspectionTypesMap
      let totalInspectionTypes = 0

      inspectionTypes.sort((a, b) => b[1] - a[1])

      // Step 4: Map chart colors
      inspectionTypes.forEach((r, index) => {
        totalInspectionTypes += r[1]

        r.push(this.h$.chartColors[this.inspectionTypes.chartColors[index]])
      });

      this.inspectionTypes.totalInspectionTypes = totalInspectionTypes
      this.inspectionTypes.data = inspectionTypes
    },

    getDashboardDataScope() {
      const dashboardGroups = this.fulcrumApp.dashboardGroups.map(item => item.data_name);

      const scopeMap = new Map();

      for (const asset of this.allAssets) {
        const group = {};

        // Build group object excluding nulls
        for (const field of dashboardGroups) {
          const val = asset[field];
          if (val != null) {
            group[field] = val;
          }
        }

        const groupKey = JSON.stringify(group);
        const valueKey = asset._created_by_name !== asset.initial_inspection_inspector ? "true" : "false";

        if (!scopeMap.has(groupKey)) {
          scopeMap.set(groupKey, { group, values: { [valueKey]: 1 } });
        } else {
          const existing = scopeMap.get(groupKey);
          existing.values[valueKey] = (existing.values[valueKey] || 0) + 1;
        }
      }

      return Array.from(scopeMap.values());
    },

    getDashboardDataStatus() {
      const dashboardGroups = this.fulcrumApp.dashboardGroups.map(item => item.data_name);

      const statusMap = new Map();

      for (const asset of this.allAssets) {
        const group = {};

        // Build group object excluding nulls
        for (const field of dashboardGroups) {
          const val = asset[field];
          if (val != null) {
            group[field] = val;
          }
        }

        const groupKey = JSON.stringify(group);
        const status = asset._status || "Undefined";

        if (!statusMap.has(groupKey)) {
          statusMap.set(groupKey, { group, values: { [status]: 1 } });
        } else {
          const entry = statusMap.get(groupKey);
          entry.values[status] = (entry.values[status] || 0) + 1;
        }
      }

      return Array.from(statusMap.values());
    },

    setOverallStatuses() {
      // count statuses
      function countStatuses(assets) {
        return assets.reduce((acc, asset) => {
          const status = asset._status;
          if (status != null) {
            acc[status] = (acc[status] || 0) + 1;
          }
          return acc;
        }, {});
      }

      // Step 1: Count statuses in assets
      let assetsStatusesCount = countStatuses(this.allAssets);

      let total = 0

      // Step 2: Aggregate by statusesMap
      let overallStatuses = Object.entries(this.overallStatuses.statusesMap).map(([group, labels]) => {
        let total = labels.reduce((sum, label) => sum + (assetsStatusesCount[label] || 0), 0);
        return [group, total];
      });

      // Step 3: Map chart colors
      overallStatuses.forEach((r, index) => {
        total += r[1]

        r.push(this.h$.chartColors[this.overallStatuses.chartColors[index]])
      });

      this.overallStatuses.totalStatuses = total;
      
      this.overallStatuses.inspectedCount = overallStatuses.reduce((sum, r) => {
        const status = r[0];
        if (status !== 'Uninspected') {
          return sum + r[1];
        }
        return sum;
      }, 0);

      const rejecteedAndPriorityCount = overallStatuses.reduce((sum, r) => {
        const status = r[0];
        if (status === 'Rejected' || status === 'Priority') {
          return sum + r[1];
        }
        return sum;
      }, 0);

      this.overallStatuses.rejectRate = this.overallStatuses.inspectedCount > 0 ? (rejecteedAndPriorityCount / this.overallStatuses.inspectedCount * 100).toFixed(2) + '%' : '0.00%';

      this.overallStatuses.data = overallStatuses;
    },

    setWeeklyStatistics() {
      const weekMap = new Map();

      // Step 1: Group assets by inspection week
      for (const asset of this.allAssets) {
        const week = asset.inspectionWeek;

        // Skip if week is null, undefined, or empty string
        if (!week || week.trim() === '') continue;

        const status = asset._status || "";
        const inspectionType = asset.inspection_type || "";

        const key = JSON.stringify({ inspectionWeek: week });

        if (!weekMap.has(key)) {
          weekMap.set(key, {
            startDate: week,
            statuses: { [status]: 1 },
            inspectionTypes: { [inspectionType]: 1 },
            inspections: 1
          });
        } else {
          const entry = weekMap.get(key);

          entry.statuses[status] = (entry.statuses[status] || 0) + 1;
          entry.inspectionTypes[inspectionType] = (entry.inspectionTypes[inspectionType] || 0) + 1;
          entry.inspections++;
        }
      }

      const weeklyStats = Array.from(weekMap.values());

      // Step 1.1: Sort weekly statistics by startDate
      weeklyStats.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

      // Step 2: Handle weekly statistics      
      let weeksStatuses = []
      let weeksInspections = []

      let totalInspections = 0

      // Step 2.1: Process weekly assets status and inspection types
      weeklyStats.forEach((week, index) => {
        totalInspections +=  week.inspections;

        weeksStatuses.push({
          startDate: week.startDate,
          statuses: week.statuses,
          inspections: week.inspections
        })

        weeksInspections.push({
          startDate: week.startDate,
          inspectionTypes: week.inspectionTypes,
          inspections: week.inspections
        })
      })

      // Step 2.1.1: Set weekly statuses
      this.weeksStatuses.inspections = totalInspections
      this.weeksStatuses.data = weeksStatuses
      this.weeksStatuses.dataViewMode = weeksStatuses.length > 1 ? 'all' : 'week'

      this.setWeekStatusData()

      if (weeksStatuses.length == 1) {
        this.weeksStatuses.currentWeek = 0
        this.weeksStatuses.currentWeekData = this.weeksStatuses.chartData[0].data.map((x) => {
          return [
            x[0],
            x[1],
            x[4]
          ]
        })
      }      

      // Step 2.1.2: Set weekly inspections
      this.weeksInspections.inspections = totalInspections
      this.weeksInspections.data = weeksInspections
      this.weeksInspections.dataViewMode = weeksInspections.length > 1 ? 'graph' : 'grid'

      this.setWeekInspectionsData()
    },

    toggleWeeklyProgressView(mode) {
      this.weeksInspections.dataViewMode = mode
    },

    setWeekInspectionsData() {
      function generateFiveStepRange(lowest, highest) {
        let roundedLowest = Math.floor(lowest / 100) * 100;  // Round down
        let roundedHighest = Math.ceil(highest / 100) * 100; // Round up

        let step = Math.ceil((roundedHighest - roundedLowest) / 4 / 100) * 100; // Determine step, rounded to nearest 100
        let range = Array.from({ length: 5 }, (_, i) => roundedLowest + i * step);

        return range;
      }

      let vm = this;

      function generateTooltip(inspectionsData) {
        if (!inspectionsData || !inspectionsData.startDate || !inspectionsData.inspectionTypes) return '';

        // Sort inspection types by count in descending order
        let inspectionTypes = Object.entries(inspectionsData.inspectionTypes).sort((a, b) => b[1] - a[1]);

        let html = `<div class="tooltip-weekly-status"><div class="tooltip-header"><span>${vm.h$.formatDate.dayMonth(inspectionsData.startDate)}</span> <strong>${inspectionsData.inspections}</strong></div>\n<ul>\n`;

        inspectionTypes.forEach(inspectionType => {
          html += `<li>${inspectionType[0]} <span>${inspectionType[1]}</span></li>\n`;
        });  

        html += `</ul></div>`;

        return html; 
      } 

      let inspections = this.weeksInspections.data.map(item => item.inspections);

      this.weeksInspections.yLabels.labels = generateFiveStepRange(Math.min(...inspections), Math.max(...inspections)).reverse()

      this.weeksInspections.xLabels.labels = this.weeksInspections.data.map((item, index) => { 
        return this.h$.formatDate.dayMonth(item.startDate)
      })

      this.weeksInspections.chartData = this.weeksInspections.data.map((item, index) => { 
        return {
          value: item.inspections,
          title: item.startDate,
          tooltip: generateTooltip(item)
        }
      })
    },

    setWeekStatusData() {
      let vm = this;
      
      function generateTooltip(inspectionDate, inspectionsData) {
        let html = `<div class="tooltip-weekly-status"><div class="tooltip-header"><span>${vm.h$.formatDate.dayMonth(inspectionDate)}</span></div>\n<ul>\n`;

        inspectionsData.forEach((item) => {
          html += `<li>${item[0]} <span>${item[1]}</span></li>\n`;
        })

        html += `</ul></div>`;

        return html; 
      } 

      this.weeksStatuses.chartData = this.weeksStatuses.data.map((item, index) => {
        let weekChartData = this.getWeekStatusData(item.statuses)

        return {
          total: weekChartData.reduce((sum, item) => sum + item[1], 0),
          week: item.startDate,
          data: weekChartData,
          tooltip: generateTooltip(item.startDate, weekChartData)
        }
      })

      this.weeksStatuses.chartTotal = this.weeksStatuses.chartData.reduce((sum, item) => sum + item.total, 0)

      let highestTotal = Math.max(...this.weeksStatuses.chartData.map(item => item.total));

      this.weeksStatuses.chartData.forEach((item) => {
        item.percentage = item.total / highestTotal * 100
      })

      this.$nextTick(() => {
        this.weeksStatuses.chartData.forEach((week, index) => {
          const p = this.$refs['poles-status-' + index];

          const t = new bootstrap.Tooltip(p[0], {
            title: week.tooltip,
            html: true,
            placement: 'bottom',
            trigger : 'hover'
          })
        })
      });
    },

    getWeekStatusData(weekStatuses) {
      let weekStatusData = []

      // Step 1: Exclude both 'Visited' and 'Not Visited'. Filter entries that start with "Y-" and contain "R" after that
      Object.entries(weekStatuses).forEach((s) => {
        if (s[0].startsWith('Y-') && /R/.test(s[0].substring(2))) {
          weekStatusData.push(s)
        }
      })

      // Step 2: Merge "-QC" variants into their base variants
      let mergedWeekStatusData = new Map();

      weekStatusData.forEach(([key, value]) => {
        let baseKey = key.endsWith("-QC") ? key.slice(0, -3) : key; // Remove "-QC" suffix

        if (mergedWeekStatusData.has(baseKey)) {
          mergedWeekStatusData.set(baseKey, mergedWeekStatusData.get(baseKey) + value);
        } else {
          mergedWeekStatusData.set(baseKey, value);
        }
      });

      // Step 3: Convert back to array
      weekStatusData = Array.from(mergedWeekStatusData);

      // Step 4: Sort arr based on weekStatusOrder array
      weekStatusData.sort((a, b) => this.weeksStatuses.chartOrder.indexOf(a[0]) - this.weeksStatuses.chartOrder.indexOf(b[0]));

      // Step 5: Map chart status colors
      const statusColorsMap = Object.fromEntries(
        Object.entries(this.weeksStatuses.chartColors).flatMap(([key, values]) => values.map(v => [v, Number(key)]))
      );

      // Step 6: Map chart user friedly status labels
      const statusLabelsMap = Object.fromEntries(
        this.weeksStatuses.statusChoices.map(choice => [choice.value, choice.label])
      );

      const total = weekStatusData.reduce((sum, item) => sum + item[1], 0)

      weekStatusData.forEach((r) => {
        let percentage = r[1] / total * 100

        r.push(r[0])
        r.push(percentage)
        r.push(this.h$.chartColors[statusColorsMap[r[0]] || 0])

        r[0] = statusLabelsMap[r[0]] || r[0]
      });
      
      return weekStatusData
    },

    showWeeksStatusDetails(i) {      
      if (i > this.weeksStatuses.chartData.length - 1) {
        i = 0
      }

      if (i < 0) {
        i = this.weeksStatuses.chartData.length - 1
      }

      this.weeksStatuses.currentWeekData = [];
      this.weeksStatuses.currentWeek = i;
      this.weeksStatuses.dataViewMode = 'week';

      this.$nextTick(() => {
        this.weeksStatuses.currentWeekData = this.weeksStatuses.chartData[i].data.map((x) => {
          return [
            x[0],
            x[1],
            x[4]
          ]
        })
      });
    },

    toggleWeeksStatusesView(mode) {
      this.weeksStatuses.dataViewMode = mode
    },

    setRatingFrequencyBars() {
      let ratingFrequency = Object.entries(JSON.parse(this.site.statistics.ratingFrequency))

      let total = 0

      ratingFrequency.forEach((r) => {
        total += r[1]
      })

      ratingFrequency.forEach((r) => {
        let percentage = r[1] / total * 100

        r.push(r[0])
        r.push(percentage)
      });

      this.ratingFrequencyStats = ratingFrequency;
    },

    getRejectedAndPriorityAssets(assets) {
      const groupFields = ["rejected_pole", "priority_pole"];
      const map = new Map();

      for (const asset of assets) {
        const group = {};

        for (const field of groupFields) {
          group[field] = asset[field]; // keep nulls
        }

        const groupKey = JSON.stringify(group);
        const restoreValue = asset.can_reinforce || "";

        if (!map.has(groupKey)) {
          map.set(groupKey, { group, values: { [restoreValue]: 1 } });
        } else {
          const entry = map.get(groupKey);
          entry.values[restoreValue] = (entry.values[restoreValue] || 0) + 1;
        }
      }

      return Array.from(map.values());
    },
    
    setRejectedAndPriorityAssets() {
      this.filteredRejectedAndPriorityAssets = this.getRejectedAndPriorityAssets(this.allAssets);
    },

    filterRejectedAndPriorityAssets() {      
      let filteredAssets = [];

      if (this.selectedInspectionTypes.length === 0) {
        filteredAssets = this.allAssets;
      } else {
        filteredAssets = this.allAssets.filter(asset => this.selectedInspectionTypes.includes(asset.inspection_type))
      }

      this.filteredRejectedAndPriorityAssets = this.getRejectedAndPriorityAssets(filteredAssets);
    },

    prepareDashboardData() {
      const dashboardGroups = this.fulcrumApp.dashboardGroups.map(item => item.data_name);
      
      const overallDefinedGroups = dashboardGroups.filter(field =>
        this.allAssets.some(asset => asset[field] != null)
      );
      
      const fallbackGroups = overallDefinedGroups.length > 0
        ? overallDefinedGroups
        : dashboardGroups.slice(0, 1);

      this.allAssets.forEach(asset => {
        // Set asset inspection week start date
        asset.inspectionWeek = asset.inspection_date ? this.m$.utc(asset.inspection_date).startOf('isoWeek').format('YYYY-MM-DD') : null;

        // Ensure at least one dashboard group is defined for the asset, otherwise set fallback "undefined" value
        const hasAnyDefinedGroup = dashboardGroups.some(field => asset[field] != null);

        if (!hasAnyDefinedGroup) {
          fallbackGroups.forEach(field => {
            asset[field] = "undefined";
          });
        }

        // Set asset top-level data group
        asset.dataGroup = asset[this.fulcrumApp.dashboardGroups[0].data_name] || null;
      });
    },
    
    setMapAssets() {
      this.filteredAssets = this.allAssets;
    },

    filterMapAssets() {
      this.filteredAssets = this.allAssets.filter(asset => this.selectedDataGroups.selected.includes(asset.dataGroup));
    },

    onInspectionTypesSelect(selected) {
      this.selectedInspectionTypes = [];
      
      selected.forEach(selectedType => {
        this.selectedInspectionTypes.push(selectedType.description);
      });

      this.filterRejectedAndPriorityAssets();
    },

    onDataGroupApproved({ groupType, groupName }) {
      this.$emit("dataGroupApproved", { groupType, groupName });
    },

    setAppData(data) {
      this.allAssets = data;

      // Prepare App Data for manipulation
      this.prepareDashboardData()

      // Handle Overall Statuses
      this.setOverallStatuses()

      // Handle rating frequency bars props
      this.setInspectionTypes()

      // Handle Rejected and Priority Poles
      this.setRejectedAndPriorityAssets()

      // Handle weekly Inspections Progress and Asset statuses
      this.setWeeklyStatistics()

      // Handle Data Groups Grid grouped by dashboard groups
      this.setDataGroups()
      
      // Handle map data
      this.setMapAssets()

      this.isSpinnerVisible = false;
      this.isInitialized = true;
    }
  },
}
</script>
